import { type PropsWithChildren, useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { useWindowSize } from "usehooks-ts";

import { useSelector } from "@/redux/hooks";
import { getUserAgent } from "@/redux/selectors/userAgent";

const MobileContentLayout = dynamic(() => import("./MobileContentLayout"), {
  ssr: true,
});

const MOBILE_WIDTH_THRESHOLD = 768;

const DesktopContentLayout = ({ children }: PropsWithChildren) => {
  return (
    <div
      className="h-full min-h-0 relative"
      id="content-layout"
      data-testid="content-layout-desktop"
    >
      {children}
    </div>
  );
};

export default ({
  children,
  mobileThreshold = MOBILE_WIDTH_THRESHOLD,
}: PropsWithChildren<{
  mobileThreshold?: number;
}>) => {
  // detect mobile from user agent
  const { isMobile: isMobilePlatform } = useSelector(getUserAgent);
  // or allow desktop mobile styling
  const { width } = useWindowSize();
  const isMobileWidth = width < mobileThreshold;

  const [isMobile, setIsMobile] = useState(isMobilePlatform);
  // this allows the client to override the mobile detection
  // without this useEffect, there will be a react hydration mismatch
  useEffect(() => {
    setIsMobile(isMobilePlatform || isMobileWidth);
  }, [isMobilePlatform, isMobileWidth]);

  const Wrapper = useMemo(
    () => (isMobile ? MobileContentLayout : DesktopContentLayout),
    [isMobile],
  );

  return <Wrapper>{children}</Wrapper>;
};
